import {
  Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

/** Minimal markdown renderer for model-authored analysis text.
 *
 * Builds React elements rather than setting innerHTML. The text comes from a
 * language model, which means it is untrusted input no matter how it was
 * produced -- rendering it as HTML would put an XSS hole directly in the page
 * that displays someone's net worth. Adding a markdown dependency for the
 * handful of constructs a model actually emits was the worse trade.
 *
 * Supports: #/##/### headings, - and * bullets, 1. ordered lists, **bold**,
 * `code`, --- rules, GFM tables, and paragraphs. Anything else renders as
 * plain text, which is a readable failure rather than a broken one.
 *
 * Tables are here because analysis output is mostly tabular -- balances by
 * account, stress tests, projections. Rendered as pipes they are unreadable,
 * which defeats the point of writing the analysis at all.
 */
export function MarkdownView({ text }: { text: string }) {
  const lines = (text || '').split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  let table: { head: string[]; rows: string[][]; align: ('left' | 'right' | 'center')[] } | null = null;

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Box key={`l${blocks.length}`} component={Tag} sx={{ pl: 3, my: 1 }}>
        {list.items.map((it, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
            {inline(it)}
          </Typography>
        ))}
      </Box>,
    );
    list = null;
  };

  const flushTable = () => {
    if (!table) return;
    const t = table;
    blocks.push(
      <Box key={`t${blocks.length}`} sx={{ overflowX: 'auto', my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {t.head.map((h, i) => (
                <TableCell key={i} align={t.align[i] ?? 'left'} sx={{ fontWeight: 600 }}>
                  {inline(h)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {t.rows.map((r, ri) => (
              <TableRow key={ri}>
                {r.map((cell, ci) => (
                  <TableCell key={ci} align={t.align[ci] ?? 'left'}>{inline(cell)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>,
    );
    table = null;
  };

  const flushPara = () => {
    if (!para.length) return;
    blocks.push(
      <Typography key={`p${blocks.length}`} variant="body2" paragraph>
        {inline(para.join(' '))}
      </Typography>,
    );
    para = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const ordered = /^\d+[.)]\s+(.*)$/.exec(line);

    if (heading) {
      flushPara(); flushList(); flushTable();
      const level = heading[1].length;
      blocks.push(
        <Typography
          key={`h${blocks.length}`}
          variant={level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1'}
          sx={{ mt: 2, mb: 1, fontWeight: 600 }}
        >
          {inline(heading[2])}
        </Typography>,
      );
    } else if (/^---+$/.test(line)) {
      flushPara(); flushList(); flushTable();
      blocks.push(<Divider key={`d${blocks.length}`} sx={{ my: 2 }} />);
    } else if (bullet) {
      flushPara(); flushTable();
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
    } else if (ordered) {
      flushPara(); flushTable();
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(ordered[1]);
    } else if (line === '') {
      flushPara(); flushList(); flushTable();
    } else if (/^\s*\|.*\|\s*$/.test(line)) {
      flushPara(); flushList();
      const cells = splitRow(line);
      if (!table) {
        table = { head: cells, rows: [], align: cells.map(() => 'left') };
      } else if (cells.every((c) => /^:?-{2,}:?$/.test(c.trim()))) {
        // The separator row also carries alignment.
        table.align = cells.map((c) => {
          const t = c.trim();
          if (t.startsWith(':') && t.endsWith(':')) return 'center';
          return t.endsWith(':') ? 'right' : 'left';
        });
      } else {
        table.rows.push(cells);
      }
    } else {
      flushList(); flushTable();
      para.push(line);
    }
  }
  flushPara(); flushList(); flushTable();

  return <Box>{blocks}</Box>;
}

/** Splits one table row into cells, dropping the outer pipes. */
function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

/** Renders **bold** and `code` inside one line. */
function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <Box key={key++} component="code"
          sx={{ px: 0.5, borderRadius: 0.5, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.85em' }}>
          {tok.slice(1, -1)}
        </Box>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
